import React from 'react';
import { Check } from '@mui/icons-material';
import { Chip, List, ListItem, ListItemButton, Stack, Typography } from '@mui/material';

// Correct and selected can both be true, so they get separate, high-contrast
// fills: solid green is "this was right", the theme's accent is "you picked
// it" — a border alone washed out on phones in dark mode, so the fill itself
// carries the signal. Dark shades (not the .main tones) so contrastText keeps
// enough contrast — see the WCAG check that shaped these choices. One lookup
// keyed by state name, so adding a state can't update the fill without also
// updating its text color.
const STATE_STYLES = {
    correct: { fill: 'success.dark', text: 'success.contrastText' },
    wrong: { fill: 'error.dark', text: 'error.contrastText' },
    selected: { fill: 'primary.main', text: null },
};

// Every prop past `question` is optional, so the host's read-only call renders
// exactly as it always has: no onSelect means no tap targets, no counts means
// no numbers.
function QuestionOptions({ question, revealed, selected, onSelect, counts, sx }) {
    const answerable = Boolean(onSelect) && !revealed;

    return (
        <List sx={sx}>
            {question.options.map((option, index) => {
                const isCorrect = revealed && index === question.correct;
                const isSelected = index === selected;
                const isSelectedWrong = isSelected && revealed && !isCorrect;

                const stateKey = isCorrect ? 'correct' : isSelectedWrong ? 'wrong' : isSelected ? 'selected' : null;
                const { fill: stateColor, text: stateTextColor } = stateKey ? STATE_STYLES[stateKey] : { fill: null, text: null };

                const content = (
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%' }}>
                        <Typography
                            sx={{
                                flexGrow: 1,
                                textAlign: 'left',
                                fontSize: '1.15rem',
                                fontWeight: isCorrect || isSelected ? 700 : 500,
                                color: stateTextColor ?? 'inherit',
                            }}
                        >
                            {option}
                            {/* Color alone shouldn't be the only signal for either state — and
                                the ui.spec.js suite asserts on this exact text, not just the fill. */}
                            {isCorrect && ' — correct answer'}
                            {isSelected && revealed && ' — your answer'}
                        </Typography>
                        {isCorrect && <Check sx={{ color: 'success.contrastText' }} />}
                        {counts && <Chip size="small" data-testid="option-count" label={counts[index]} />}
                    </Stack>
                );

                const itemSx = {
                    border: isSelected && !revealed ? '3px solid' : '2px solid',
                    borderColor: stateColor ?? 'divider',
                    bgcolor: stateColor ?? 'transparent',
                    borderRadius: 2,
                    py: 1.25,
                    px: 2,
                    mb: 1.5,
                };

                return answerable ? (
                    <ListItemButton key={option} sx={itemSx} selected={isSelected} onClick={() => onSelect(index)}>
                        {content}
                    </ListItemButton>
                ) : (
                    <ListItem key={option} sx={itemSx}>
                        {content}
                    </ListItem>
                );
            })}
        </List>
    );
}

export default QuestionOptions;
